import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiBookOpen, FiClock, FiFileText } from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import './FlashcardDetailView.css';

/**
 * FlashcardDetailView - Detailed info view for a selected flashcard deck
 * 
 * Shown in the workspace when a deck is selected from the sidebar list.
 * Features: editable title, delete, source documents, deck stats, primary study button.
 */
const FlashcardDetailView = ({ deck, onStudyDeck, onBack }) => {
  const { getDocumentById, getDeckStats, updateFlashcardDeck, deleteFlashcardDeck, clearCache } = useData();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [docsLoading, setDocsLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Inline rename state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(deck.title || '');
  const [title, setTitle] = useState(deck.title || 'Untitled Flashcard Set');

  const deckId = deck.deck_id || deck.id;

  // Load deck stats
  useEffect(() => {
    if (!deckId) return;
    let cancelled = false;

    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const data = await getDeckStats(deckId);
        if (!cancelled) setStats(data);
      } catch (err) {
        console.warn('Failed to load deck stats:', err);
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };

    loadStats();
    return () => { cancelled = true; };
  }, [deckId, getDeckStats]);

  // Load source documents
  useEffect(() => {
    const docIds = deck.documentIds || [];
    if (docIds.length === 0) {
      setDocsLoading(false);
      return;
    }
    let cancelled = false;

    const loadDocs = async () => {
      setDocsLoading(true);
      try {
        const docPromises = docIds.map(docId => getDocumentById(docId));
        const docs = await Promise.all(docPromises);
        if (!cancelled) setDocuments(docs.filter(Boolean));
      } catch (err) {
        console.error('Failed to load documents:', err);
        if (!cancelled) setDocuments([]);
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    };

    loadDocs();
    return () => { cancelled = true; };
  }, [deck.documentIds, getDocumentById]);

  // Sync title when deck prop changes  
  useEffect(() => {
    setTitle(deck.title || 'Untitled Flashcard Set');
    setEditTitle(deck.title || '');
  }, [deck.title]);

  const handleSaveTitle = useCallback(async () => {
    const trimmed = editTitle.trim();
    if (!trimmed || trimmed === title) {
      setIsEditing(false);
      setEditTitle(title);
      return;
    }

    try {
      await updateFlashcardDeck(deckId, { title: trimmed });
      setTitle(trimmed);
      clearCache('flashcard-decks');
      addToast({ intent: 'success', message: 'Deck renamed' });
    } catch (err) {
      console.error('Failed to rename deck:', err);
      addToast({ intent: 'danger', message: 'Failed to rename deck' });
      setEditTitle(title);
    }
    setIsEditing(false);
  }, [editTitle, title, deckId, updateFlashcardDeck, clearCache, addToast]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete this deck?')) return;

    try {
      await deleteFlashcardDeck(deckId);
      clearCache('flashcard-decks');
      addToast({ intent: 'success', message: 'Deck deleted' });
      if (onBack) onBack();
    } catch (err) {
      console.error('Failed to delete deck:', err);
      addToast({ intent: 'danger', message: 'Failed to delete deck' });
    }
  }, [deckId, deleteFlashcardDeck, clearCache, addToast, onBack]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 0) return 'just now';
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flashcard-detail-view">
      {/* Header: Title + Actions */}
      <div className="fdv-header">
        <div className="fdv-title-row">
          {isEditing ? (
            <div className="fdv-edit-title">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') { setIsEditing(false); setEditTitle(title); }
                }}
                onBlur={handleSaveTitle}
                className="fdv-edit-input"
                autoFocus
              />
            </div>
          ) : (
            <h2 className="fdv-title" onClick={() => { setIsEditing(true); setEditTitle(title); }}>
              {title}
              <FiEdit2 className="fdv-title-edit-icon" size={16} />
            </h2>
          )}

          <div className="fdv-actions">
            <button className="fdv-delete-btn" onClick={handleDelete} title="Delete deck">
              <FiTrash2 size={16} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      {/* Info Bar */}
      <div className="fdv-info-bar">
        <div className="fdv-info-item">
          <FiFileText size={14} />
          <span>{deck.docCount || deck.documentIds?.length || 0} document{(deck.docCount || deck.documentIds?.length || 0) !== 1 ? 's' : ''}</span>
        </div>
        <div className="fdv-info-item">
          <FiClock size={14} />
          <span>{formatDate(deck.createdAt || deck.generatedAt)}</span>
        </div>
      </div>

      {/* Primary Action */}
      <div className="fdv-action-section">
        <button
          className="fdv-primary-btn"
          onClick={() => onStudyDeck && onStudyDeck(deck)}
        >
          <FiBookOpen size={18} />
          Study Deck
        </button>
      </div>

      {/* Stats */}
      {!statsLoading && stats && (
        <div className="fdv-section">
          <h3 className="fdv-section-title">Study Progress</h3>
          <div className="fdv-stats-grid">
            <div className="fdv-stat-card">
              <div className="fdv-stat-value">{stats.totalCards || 0}</div>
              <div className="fdv-stat-label">Total Cards</div>
            </div>
            <div className="fdv-stat-card">
              <div className="fdv-stat-value fdv-stat--due">{stats.dueCards || 0}</div>
              <div className="fdv-stat-label">Due for Review</div>
            </div>
            <div className="fdv-stat-card">
              <div className="fdv-stat-value fdv-stat--mastered">{stats.masteredCards || 0}</div>
              <div className="fdv-stat-label">Mastered</div>
            </div>
          </div>
        </div>
      )}

      {/* Source Documents */}
      <div className="fdv-section">
        <h3 className="fdv-section-title">Source Documents</h3>
        {docsLoading ? (
          <div className="fdv-loading-inline">Loading documents...</div>
        ) : documents.length === 0 ? (
          <div className="fdv-empty-inline">No source documents</div>
        ) : (
          <div className="fdv-doc-list">
            {documents.map(doc => (
              <div
                key={doc.id}
                className="fdv-doc-item fdv-doc-item--clickable"
                onClick={() => navigate(`/document/${doc.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/document/${doc.id}`); } }}
              >
                <span className="fdv-doc-icon">📄</span>
                <span className="fdv-doc-name">{doc.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashcardDetailView;
